import { useState, useRef } from "react";
import useSWRInfinite from "swr/infinite";
import useSWR from "swr";
import {
  Car,
  CarsResponse,
} from "../../data/bruktbilsalgBackend/types/car.types";
import { useEffect, useMemo } from "react";
import { getQueryParamsFromFilters } from "../filters/utils/queryParamFilterUtils";
import {
  filterQueryKeys,
} from "../filters/types/filtersQuery.types";
import { useFilters } from "../filters/hooks/useFilters";
import { getDepartmentsBySlug } from "@/data/forhandler/departments";
import { SortOption, getSortQueryParam } from "./sorting";

const DEPARTMENTIDS_QUERY_PARAM = "DepartmentIds";

// Set of all query param keys managed by the filter system.
// Any params NOT in this set are preserved as-is in the URL and forwarded to the API.
const MANAGED_FILTER_KEYS = new Set(Object.values(filterQueryKeys));

type PaginatedResult = {
  mode: "paginated";
  cars: Car[];
  carsCount: number | null;
  error: Error | undefined;
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  hasMoreCars: boolean;
  sortOption: SortOption;
  setSortOption: (option: SortOption) => void;
};

type InfiniteResult = {
  mode: "infinite";
  cars: Car[];
  carsCount: number | null;
  error: Error | undefined;
  isLoading: boolean;
  size: number;
  setSize: (size: number | ((size: number) => number)) => void;
  hasMoreCars: boolean;
  sortOption: SortOption;
  setSortOption: (option: SortOption) => void;
};

type CarFetcherResult = PaginatedResult | InfiniteResult;

function getActiveDepartmentIdFilters(
  initialDepartmentIds: string[] | undefined,
  filterDepartmentIds: string[],
): string[] {
  if (!initialDepartmentIds?.length) return filterDepartmentIds;
  if (!filterDepartmentIds.length) return initialDepartmentIds ?? [];

  // Only allow further filtering from initialDepartmentIds if it's not empty
  return filterDepartmentIds.filter((depId) =>
    initialDepartmentIds.includes(depId),
  );
}

export const fetcher = async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`An error occurred while fetching data from url: ${url}`);
  }

  return res.json();
};

export const useCarFetcher = (
  initialDepartmentIds?: string[],
  pageSize = 15,
  isPaginated = false,
): CarFetcherResult => {
  const [page, setPage] = useState(1);
  const [sortOption, setSortOption] = useState<SortOption>("published");

  const {
    brandsWithModels,
    fuelFilter,
    driveWheelFilter,
    priceRange,
    modelYearRange,
    mileageRange,
    departmentsFilter,
    equipmentFilter,
    salesTypeFilter,
    colorFilter,
    availabilityFilter
  } = useFilters();

  const carFilterQueryParams = useMemo(
    () =>
      getQueryParamsFromFilters(
        brandsWithModels,
        fuelFilter,
        driveWheelFilter,
        priceRange,
        modelYearRange,
        mileageRange,
        departmentsFilter,
        equipmentFilter,
        salesTypeFilter,
        colorFilter,
        availabilityFilter
      ),
    [
      brandsWithModels,
      fuelFilter,
      driveWheelFilter,
      priceRange,
      modelYearRange,
      mileageRange,
      departmentsFilter,
      equipmentFilter,
      salesTypeFilter,
      colorFilter,
      availabilityFilter
    ],
  );

  const activeDepartmentSlugs =
    departmentsFilter && departmentsFilter.length > 0
      ? (() => {
          // Get departments that are individually selected
          const individuallySelected = departmentsFilter
            .filter((d) => d.isActive)
            .map((d) => d.key);

          // Get all departments from expanded groups that have no individual selections
          const expandedGroups = new Set(
            departmentsFilter
              .filter((d) => d.isGroupExpanded && d.group)
              .map((d) => d.group),
          );

          // For each expanded group, check if any department in that group is individually selected
          const groupsWithSelections = new Set(
            departmentsFilter
              .filter((d) => d.isActive && d.group)
              .map((d) => d.group),
          );

          // Get all departments from expanded groups that have NO individual selections
          const fromExpandedGroupsWithoutSelection = departmentsFilter
            .filter(
              (d) =>
                d.group &&
                expandedGroups.has(d.group) &&
                !groupsWithSelections.has(d.group),
            )
            .map((d) => d.key);

          return [...individuallySelected, ...fromExpandedGroupsWithoutSelection];
        })()
      : [];
  const filterDepartmentIds =
    activeDepartmentSlugs.length > 0
      ? activeDepartmentSlugs
          .flatMap((slug) => getDepartmentsBySlug(slug))
          .map((dep) => dep.id)
      : [];
  const activeDepartmentIds = filterDepartmentIds
    ? getActiveDepartmentIdFilters(initialDepartmentIds, filterDepartmentIds)
    : [];

  const departmentQuery =
    activeDepartmentIds
      ?.map((depId) => `&${DEPARTMENTIDS_QUERY_PARAM}=${depId}`)
      .join("") ?? "";

  const sortQuery = getSortQueryParam(sortOption);

  // Capture non-filter query params from the initial URL (e.g. ExternalReferenceId).
  // These are preserved in the URL and forwarded to the API.
  const passThroughParams = useRef<string>("");
  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    const preserved: string[] = [];
    currentParams.forEach((value, key) => {
      if (!MANAGED_FILTER_KEYS.has(key)) {
        preserved.push(`&${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });
    passThroughParams.current = preserved.join("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only read on mount — these are one-time URL params

  // Use useSWR for paginated mode, useSWRInfinite for infinite scroll
  const paginatedData = useSWR<CarsResponse, Error>(
    isPaginated
      ? `/api/cars?offset=${(page - 1) * pageSize}&limit=${pageSize}${carFilterQueryParams}${departmentQuery}${sortQuery}${passThroughParams.current}`
      : null,
    fetcher,
  );

  const infiniteData = useSWRInfinite<CarsResponse, Error>(
    !isPaginated
      ? (pageIndex) =>
          `/api/cars?offset=${pageIndex * pageSize}&limit=${pageSize}${carFilterQueryParams}${departmentQuery}${sortQuery}${passThroughParams.current}`
      : () => null,
    fetcher,
    {
      revalidateFirstPage: false,
      parallel: true,
    },
  );

  // Set active filters as query params in URL, preserving non-filter params
  useEffect(() => {
    const url = new URL(
      window.location.origin + window.location.pathname,
    ).toString();

    const allParams = [carFilterQueryParams, passThroughParams.current]
      .filter(Boolean)
      .join("");

    window.history.replaceState(
      window.history.state,
      "",
      `${url}${allParams.length > 0 ? `?${allParams}` : ""}`,
    );
  }, [carFilterQueryParams]);

  // Reset to page 1 when filters change (paginated mode only)
  const prevFiltersRef = useRef<string>("");
  const currentFilters = carFilterQueryParams;

  useEffect(() => {
    if (
      isPaginated &&
      prevFiltersRef.current !== "" &&
      prevFiltersRef.current !== currentFilters
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPage(1);
    }
    prevFiltersRef.current = currentFilters;
  }, [isPaginated, currentFilters]);

  if (isPaginated) {
    const { data, error, isLoading } = paginatedData;
    const cars = data?.cars ?? [];
    const carsCount = data?.numCars ?? null;
    const hasMoreCars = carsCount !== null && carsCount > page * pageSize;

    if (error) {
      throw Error(error.message);
    }

    return {
      mode: "paginated" as const,
      cars: cars.flat(),
      carsCount,
      error,
      isLoading,
      page,
      setPage,
      hasMoreCars,
      sortOption,
      setSortOption,
    };
  }

  // Infinite scroll mode
  const { data, error, isLoading, size, setSize } = infiniteData;
  const cars = data?.flatMap((page) => page.cars) ?? [];
  const carsCount = data?.[0]?.numCars ?? null;
  const hasMoreCars =
    data && data.length > 0 ? data[data.length - 1].cars.length > 0 : true;

  if (error) {
    throw Error(error.message);
  }

  return {
    mode: "infinite" as const,
    cars: cars.flat(),
    carsCount,
    error,
    isLoading,
    size: size ?? 1,
    setSize,
    hasMoreCars,
    sortOption,
    setSortOption,
  };
};
